import { useEffect, useState, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { RealtimeChannel } from '@supabase/supabase-js';

export interface UserPresence {
  user_id: string;
  email: string;
  name: string | null;
  avatar_url: string | null;
  online_at: string;
  current_subphase?: string;
  is_typing?: boolean;
  last_step_at?: string;
}

export function useSessionPresence(sessionId: string | undefined, currentUserId: string | null) {
  const [activeUsers, setActiveUsers] = useState<UserPresence[]>([]);
  const [channel, setChannel] = useState<RealtimeChannel | null>(null);

  useEffect(() => {
    if (!sessionId || !currentUserId) {
      return;
    }

    const roomChannel = supabase.channel(`session_${sessionId}`);

    roomChannel
      .on('presence', { event: 'sync' }, () => {
        const state = roomChannel.presenceState();
        const users: UserPresence[] = [];
        
        Object.keys(state).forEach((key) => {
          const presences = state[key];
          presences.forEach((presence: any) => {
            users.push(presence);
          });
        });
        
        // Filter out current user
        setActiveUsers(users.filter(u => u.user_id !== currentUserId));
      })
      .on('presence', { event: 'join' }, ({ key, newPresences }) => {
        console.log('User joined:', key, newPresences);
      })
      .on('presence', { event: 'leave' }, ({ key, leftPresences }) => {
        console.log('User left:', key, leftPresences);
      });

    roomChannel.subscribe(async (status) => {
      if (status === 'SUBSCRIBED') {
        // Get current user profile
        const { data: profile } = await supabase
          .from('profiles')
          .select('email, name, avatar_url')
          .eq('id', currentUserId)
          .single();

        if (profile) {
          const userStatus: UserPresence = {
            user_id: currentUserId,
            email: profile.email,
            name: profile.name,
            avatar_url: profile.avatar_url,
            online_at: new Date().toISOString(),
            current_subphase: undefined,
            is_typing: false,
            last_step_at: undefined,
          };

          await roomChannel.track(userStatus);
        }
      }
    });

    setChannel(roomChannel);

    return () => {
      roomChannel.untrack();
      supabase.removeChannel(roomChannel);
    };
  }, [sessionId, currentUserId]);

  // Update presence fields without re-subscribing
  const updatePresence = useCallback(async (fields: Partial<Pick<UserPresence, 'current_subphase' | 'is_typing' | 'last_step_at'>>) => {
    if (!channel) return;
    const state = channel.presenceState();
    // Find our own presence to merge
    let current: any = {};
    Object.values(state).forEach((presences: any) => {
      presences.forEach((p: any) => {
        if (p.user_id === currentUserId) {
          current = p;
        }
      });
    });
    await channel.track({ ...current, ...fields });
  }, [channel, currentUserId]);

  return { activeUsers, updatePresence };
}
